import React from 'react';
import { JsonLd } from 'react-schemaorg';
import map from 'lodash/map';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import lowerCase from 'lodash/lowerCase';
import forEach from 'lodash/forEach';
import { I18nHoc } from 'helpers/i18n';
import { dateLife } from 'helpers/formatDate';
import absoluteUrl from 'helpers/absoluteUrl';
import { humanizeBigNumbers } from 'helpers/numDesc';
import { FavoritesButton } from 'subsys/favorites';
import Link from 'components/link';
import Row, { Col } from 'components/row';
import Icon, { iconTypes } from 'components/icon';
import TabJsControl from 'components/tab-js-control';
import { AbstractObjectTile, TileView } from 'components/layouts';
import { getToponyms } from 'components/layouts/abstract-object-tile';
import InteractiveComponent from 'components/interactive-fly-component';

import linksBild from 'src-sites/bild/links';
import { reliabilityIndex } from 'src-sites/bild/section-project/reliability-index';

import translates from './card-i18n';

import { mergePrice } from '../section-project/render-common';
import { checkSold } from '../section-project/cards-object/list-card';

import 'styles/bild/section-projects/_view-list.scss';

@I18nHoc(translates())
export default class ListCard extends AbstractObjectTile {
  static displayName = 'ListCard';

  static getDerivedStateFromProps(props, state) {
    const projectId = get(props, 'object.id');

    if (state.projectId !== projectId) return { projectId, selectedTab: null };

    return null;
  }

  constructor(props, ctx) {
    super(props, ctx);

    this.imagesKey = 'bild';
    this.state = {};
  }

  checkForEmptyData(data) {
    if (this.props.checkForEmptyData) return this.props.checkForEmptyData(data);

    const price = mergePrice(data);

    return (
      !data.bed_places &&
      !data.paramsShortText &&
      // !data.complex_sites &&
      !data.built_quarter &&
      !data.built_year &&
      !price.mainPrice
    );
  }

  renderImage(data, bemClasses) {
    const { url, toponym, geometry, geo, points, type, status_ready } = data;
    const header = data.display_name || data.location_path;
    const displayLocality = get(data, 'display_locality', null);
    const image = get(data, 'title_image.id');
    const alt = data.display_name || data.location_path;
    const title = data.name || null;

    return (
      <>
        {super.renderImage(data, bemClasses, false, false, true)}
        <div {...bemClasses('onPhoto', 'data')}>
          <div {...bemClasses('onPhotoOverlay', 'bottom')}>
            <Row {...bemClasses('dataInfo')}>
              <Col {...bemClasses('header')} allAuto allPr={0}>
                {header && <span {...bemClasses('text')} data-test="location" children={header} />}
              </Col>
              <Col {...bemClasses('reliability')} allAuto allPr={0} children={reliabilityIndex.call(this, bemClasses, points, type, status_ready)} />
              <Col {...bemClasses('dataBottom', 'inline')} allPr={0} children={getToponyms.call(this, displayLocality, toponym, geometry || geo, bemClasses)} />
            </Row>
          </div>
        </div>
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ApartmentComplex',
            name: header,
            url: absoluteUrl(url),
            image: absoluteUrl(`/images/thumb/full/${image}`)
          }}
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: alt || null,
            description: title,
            contentLocation: get(data, 'parent_display_path') ? get(data, 'parent_display_path') : null,
            contentUrl: image ? absoluteUrl(`/images/thumb/full/${image}`) : null
          }}
        />
      </>
    );
  }

  renderTabTitle(data) {
    return get(data, 'value.abbrev', '') || get(data, 'value.value_short', '') || get(data, 'value.entity_short', '');
  }

  renderTileLinkContent(bemClasses, data = {}, soldData, title = false, sales_suspended_until) {
    const { i18n } = this;
    let titleTxt = i18n('defaultPriceText');
    let priceText = i18n('defaultPriceText');
    const sold = checkSold.call(this, null, soldData, data.group);
    const topTxt = data.minArea ? `${i18n('textFrom')}\u00a0${data.minArea}\u00a0м²` : '';
    let bottomSoldTxt = sold.sold ? sold.soldTxt : null;

    if (!bottomSoldTxt && data.allSold) bottomSoldTxt = i18n('allSoldTile');
    if (!sold.sold && sales_suspended_until) bottomSoldTxt = i18n('salesSuspendedUntil');
    if (data.minPrice) {
      priceText = `${i18n('textFrom')}\u0020${humanizeBigNumbers.call(this, data.minPrice)}\u00a0${lowerCase(get(data, 'priceCur.name_short') || get(data, 'priceCur.id', ''))}`;
      titleTxt = `${topTxt}\u0020${priceText}`;
    }
    if (title) return titleTxt;

    return (
      <>
        <span {...bemClasses('arrow', 'top right')}>
          <span {...bemClasses('arrowLine')} />
        </span>
        <span {...bemClasses('text', { top: true, sold: bottomSoldTxt })} children={topTxt} data-test="textTop" />
        <span {...bemClasses('text', { price: true, sold: bottomSoldTxt })} children={bottomSoldTxt || priceText} data-test="textPrice" />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'Offer',
            price: data.minPrice,
            priceCurrency: get(data, 'priceCur.name_standart'),
            availability: (data.allSold || sold.sold || sales_suspended_until) ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock'
          }}
        />
      </>
    );
  }

  changeSelectedTab = (data, key) => {
    this.setState({ selectedTab: key });
  };

  renderContent(data = {}, bemClasses) {
    const { i18n } = this;
    const {
      object_landings, date_life,
      all_flats_sold, all_commercial_sold, all_house_and_area_sold,
      sales_suspended_until,
      // url, toponym, geometry, geo,
      // reliability_for_whom, points, complex_sites
    } = data;
    // const image = get(data, 'title_image.id');
    // const displayLocality = get(data, 'display_locality', null);
    // const txt = reliability_for_whom ? reliability_for_whom.replace(/<\/?[^>]+>/g, ' ') : '';
    // const header = data.display_name || data.location_path;
    const soldData = { all_flats_sold, all_commercial_sold, all_house_and_area_sold };

    let selectedTab = this.state.selectedTab;
    if (selectedTab === null) {
      forEach(object_landings, (it, key) => {
        if (selectedTab === null && !get(it, 'data.allSold')) selectedTab = key;
      });
    }
    if (selectedTab === null) selectedTab = 0;

    return (
      <>
        {object_landings && object_landings.length > 0 &&
          <TabJsControl
            {...bemClasses('tab', 'data')}
            selectedTab={selectedTab}
            onChange={this.changeSelectedTab}
            aside={
              <span {...bemClasses('text', 'aside')} children={i18n('price')} />
            }
          >
            {map(object_landings, (val, key) => (
              <tab
                title={this.renderTabTitle(val.data)}
                key={key}
              >
                <Link
                  {...bemClasses('link', 'price')}
                  builderProps={{
                    ...val,
                    title: this.renderTileLinkContent(
                      bemClasses,
                      val.data,
                      soldData,
                      true,
                      sales_suspended_until
                    )
                  }}
                  builder={linksBild.sectionLanding}
                  external
                >
                  <span
                    {...bemClasses('linkContent')}
                    children={this.renderTileLinkContent(
                      bemClasses,
                      val.data,
                      soldData,
                      false,
                      sales_suspended_until
                    )}
                  />
                </Link>
              </tab>
            ))}
          </TabJsControl>
        }
        {/* builtDate &&
          <div {...bemClasses('dataBottom', 'builtDate')} style={{ marginBottom: '1rem' }}>
            {renderCommonItem(bemClasses, builtDate)}
          </div>
        }
        {complex_sites &&
          <div {...bemClasses('dataBottom', 'links')}>
            {renderCommonLinkItem(bemClasses, mergeComplexSites.func({ complex_sites }, true))}
          </div>
        */}
        <div {...bemClasses('dateLife')}>
          {date_life &&
            <span {...bemClasses('text', 'dateLife')}>
              <Icon {...bemClasses('icon', 'checkCircleBild')} type={iconTypes.checkCircleBild} />
              {i18n('verified')} {dateLife.call(this, date_life)}
            </span>
          }
        </div>
      </>
    );
  }

  // Render Favourite btns
  getInteractiveComponents(bemClasses) {
    const { object, favData } = this.props;
    const index = findIndex(favData, { entity_id: object.id });

    return (
      <InteractiveComponent
        {...bemClasses('favorite')}
        iconType={iconTypes.favoritesBorder}
        newIconType={iconTypes.favorites}
        destinationPoint="favoritesIcon"
      >
        <FavoritesButton
          project="bild"
          entity="location"
          entityId={object.id}
          subgroup="living_complex"
          inFavorites={index > -1}
          hideLabel
        />
      </InteractiveComponent>
    );
  }

  // TileView SideBlock
  renderSideBlock(data, bemClasses) {
    if (!this.state.clientSide) return null;

    return (
      <>
        {/* {super.renderSideBlock(data, bemClasses)} */}
        {this.getInteractiveComponents(bemClasses)}
      </>
    );
  }

  render() {
    const { object, dataMods, ...restProps } = this.props;

    return (
      <TileView
        {...restProps}
        data={object}
        // tileRef={el => this.tileRef = el}
        dataMods={{ ...dataMods, fill: false, bild: true, list: true }}
        onMouseOver={restProps.onMouseOver || this.handleHover}
        onMouseOut={restProps.onMouseOut || this.handleUnhover}
        urlBuilder={linksBild.sectionLanding}
        linkCard={{ filledCard: true, filledImg: false }}
        renderImage={this.renderImage}
        renderContent={this.renderContent}
        renderSideBlock={this.renderSideBlock}
        // renderUnderneath={this.renderUnderneath}
      />
    );
  }
}
